/**
 * Modale BATCH : reporte max(SO, SR) au devoir UNIQUEMENT pour ceux à 0 au devoir
 * + ayant composé l'examen, sur TOUTES les matières d'un scope (pôle, niveau,
 * option, classe). Au moins un filtre de scope est obligatoire — le backend
 * refuse l'appel autrement (anti-erreur de masse).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Loader, AlertTriangle, CheckCircle2, ArrowRightLeft, Info, Filter,
} from 'lucide-react';
import { useReporterExamenSiDevoirZeroBatchMutation } from '../../services/notesDevoirsApi';
import { useGetPolesQuery, useGetOptionsQuery, useGetClassesQuery } from '../../services/polesOptionsApi';

interface Props {
  open: boolean;
  anneeAcademique: string;
  semestreDefaut?: 'S1' | 'S2';
  // Pré-remplissage optionnel (utile quand on vient d'une vue déjà filtrée)
  scopeDefaut?: { pole?: string; niveau?: string; option?: string; classe?: string };
  onClose: () => void;
  onSuccess?: () => void;
}

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];

const ReporterExamenSiZeroBatchModal: React.FC<Props> = ({
  open, anneeAcademique, semestreDefaut = 'S1', scopeDefaut, onClose, onSuccess,
}) => {
  const [semestre, setSemestre] = useState<'S1' | 'S2'>(semestreDefaut);
  const [pole, setPole] = useState(scopeDefaut?.pole || '');
  const [niveau, setNiveau] = useState(scopeDefaut?.niveau || '');
  const [option, setOption] = useState(scopeDefaut?.option || '');
  const [classe, setClasse] = useState(scopeDefaut?.classe || '');

  const { data: poles } = useGetPolesQuery({ actif: true });
  const { data: options } = useGetOptionsQuery({ actif: true });
  const { data: classes } = useGetClassesQuery({ actif: true });

  const optionsFiltrees = useMemo(() => {
    const all = (options || []) as any[];
    if (!pole) return all;
    const p = (poles || []).find((x: any) => x.nom === pole);
    if (!p) return all;
    return all.filter((o: any) => o.pole === p.id);
  }, [options, poles, pole]);

  const [reporter, { isLoading }] = useReporterExamenSiDevoirZeroBatchMutation();
  const [preview, setPreview] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<number | null>(null);
  const [confirmer, setConfirmer] = useState(false);
  const [filtreSansEligibles, setFiltreSansEligibles] = useState(true);

  // Reset état à l'ouverture
  useEffect(() => {
    if (!open) {
      setPreview(null); setError(null); setApplied(null); setConfirmer(false);
    }
  }, [open]);

  const scopeActif = !!(pole || niveau || option || classe);

  const handlePreview = async () => {
    if (!scopeActif) {
      setError('Sélectionnez au moins un filtre (pôle, niveau, option ou classe) avant de prévisualiser.');
      return;
    }
    setError(null); setApplied(null); setConfirmer(false);
    try {
      const res = await reporter({
        annee_academique: anneeAcademique,
        semestre,
        pole: pole || undefined,
        niveau: niveau || undefined,
        option: option || undefined,
        classe: classe || undefined,
        dry_run: true,
      }).unwrap();
      setPreview(res);
    } catch (e: any) {
      setError(e?.data?.error || e?.message || 'Erreur de prévisualisation');
    }
  };

  const handleAppliquer = async () => {
    if (!scopeActif) return;
    setError(null);
    try {
      const res = await reporter({
        annee_academique: anneeAcademique,
        semestre,
        pole: pole || undefined,
        niveau: niveau || undefined,
        option: option || undefined,
        classe: classe || undefined,
        dry_run: false,
      }).unwrap();
      setApplied(res.total_appliques);
      setPreview(res);
      // onSuccess déclenche des refetch qui peuvent throw (cache RTK).
      // Ne pas laisser ces erreurs masquer le succès de l'opération.
      try {
        if (onSuccess) onSuccess();
      } catch (e) {
        console.warn('Refetch après application a échoué (sans impact):', e);
      }
    } catch (e: any) {
      setError(e?.data?.error || e?.message || 'Erreur lors de l\'application');
    }
  };

  if (!open) return null;

  const parMatiere = preview?.par_matiere || [];
  const matieresAffichees = filtreSansEligibles
    ? parMatiere.filter((m: any) => m.nb_eligibles > 0)
    : parMatiere;
  const ignoresGlobaux = preview?.ignores_globaux || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-amber-600 dark:text-amber-400" />
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                Reporter examen → devoir EN MASSE
              </h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Applique « max(SO, SR) » à toutes les matières d'un scope donné.
                {' '}Seuls les étudiants à <b>moyenne devoir = 0</b> sont touchés.
              </p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-400">
            <X size={16} />
          </button>
        </div>

        {/* Scope selectors */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-amber-50/40 dark:bg-amber-900/10">
          <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
            <Filter size={13} /> Scope (au moins un filtre obligatoire)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div>
              <label className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">Semestre *</label>
              <select value={semestre} onChange={(e) => setSemestre(e.target.value as any)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="S1">S1</option>
                <option value="S2">S2</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">Pôle</label>
              <select value={pole} onChange={(e) => { setPole(e.target.value); setOption(''); }}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">—</option>
                {(poles || []).map((p: any) => <option key={p.id} value={p.nom}>{p.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">Niveau</label>
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">—</option>
                {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">Option</label>
              <select value={option} onChange={(e) => setOption(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">—</option>
                {optionsFiltrees.map((o: any) => <option key={o.id} value={o.nom}>{o.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase text-gray-500 mb-0.5">Classe</label>
              <select value={classe} onChange={(e) => setClasse(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">—</option>
                {(classes || []).map((c: any) => <option key={c.id || c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
            <div className="text-[10px] text-gray-500 dark:text-gray-400">
              {scopeActif ? (
                <>Scope : <b>{[pole, niveau, option, classe].filter(Boolean).join(' · ')}</b> ({semestre})</>
              ) : (
                <span className="text-red-500">⚠ Sélectionnez au moins un filtre</span>
              )}
            </div>
            <button onClick={handlePreview}
              disabled={!scopeActif || isLoading}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded font-medium flex items-center gap-1.5">
              {isLoading && <Loader size={12} className="animate-spin" />}
              Prévisualiser
            </button>
          </div>
        </div>

        {/* Règles */}
        <div className="px-5 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30 text-[11px] text-blue-900 dark:text-blue-200">
          <div className="flex items-start gap-2">
            <Info size={12} className="mt-0.5 flex-shrink-0" />
            Règles : <b>moyenne_devoirs = 0</b> + <b>non verrouillé</b> + a composé SO ou SR + max(SO,SR) {'>'} 0. Les étudiants déjà notés ne sont jamais touchés.
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="flex items-start gap-2 p-3 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}

          {applied !== null && (
            <div className="flex items-start gap-2 p-3 mb-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold">{applied} note(s) de devoir mise(s) à jour.</div>
                <div className="text-xs">
                  Réparties sur {parMatiere.filter((m: any) => m.nb_appliques > 0).length} matière(s).
                </div>
              </div>
            </div>
          )}

          {!preview && !error && !applied && (
            <div className="text-center text-sm text-gray-500 py-12">
              Choisissez votre scope puis cliquez sur <b>Prévisualiser</b> pour voir
              ce qui sera modifié avant d'appliquer.
            </div>
          )}

          {preview && (
            <>
              {/* KPIs globaux */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                <Kpi label="Matières" value={preview.total_matieres}
                  couleur="text-gray-700 dark:text-gray-300" />
                <Kpi label="Éligibles" value={preview.total_eligibles}
                  couleur="text-green-600 dark:text-green-400" />
                <Kpi label="Verrouillés" value={ignoresGlobaux.verrouilles}
                  couleur="text-gray-500" />
                <Kpi label="Déjà notés" value={ignoresGlobaux.deja_note}
                  couleur="text-blue-600 dark:text-blue-400" hint="intacts (moy > 0)" />
                <Kpi label="Sans examen" value={ignoresGlobaux.pas_examen}
                  couleur="text-gray-500" />
              </div>

              {/* Filtre d'affichage */}
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Détail par matière ({matieresAffichees.length}/{parMatiere.length})
                </h3>
                <label className="text-[11px] text-gray-600 dark:text-gray-400 flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={filtreSansEligibles}
                    onChange={(e) => setFiltreSansEligibles(e.target.checked)} />
                  Masquer les matières sans éligibles
                </label>
              </div>

              {/* Table par matière */}
              {parMatiere.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-10">
                  Aucune matière à traiter dans ce scope.
                </div>
              ) : (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-[10px] uppercase tracking-wide text-gray-500 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-2 text-left">Matière</th>
                        <th className="px-2 py-2 text-left">Classes</th>
                        <th className="px-2 py-2 text-right">Éligibles</th>
                        {applied !== null && (
                          <th className="px-2 py-2 text-right">Appliqués</th>
                        )}
                        <th className="px-2 py-2 text-right text-gray-400">Sans examen</th>
                        <th className="px-2 py-2 text-right text-gray-400">0 partout</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {matieresAffichees.map((m: any) => (
                        <tr key={m.matiere_id}
                          className={m.nb_eligibles > 0
                            ? 'bg-amber-50/40 dark:bg-amber-900/10'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}>
                          <td className="px-2 py-1.5">
                            <div className="font-medium text-gray-900 dark:text-white truncate max-w-[280px]" title={m.nom}>
                              {m.nom}
                            </div>
                            <div className="text-[10px] font-mono text-gray-500">{m.code}</div>
                          </td>
                          <td className="px-2 py-1.5 text-[11px] text-gray-600 dark:text-gray-400">
                            {m.classes.join(', ') || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-bold text-green-600 dark:text-green-400">
                            {m.nb_eligibles}
                          </td>
                          {applied !== null && (
                            <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                              {m.nb_appliques}
                            </td>
                          )}
                          <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                            {m.ignores.pas_examen}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                            {m.ignores.pas_amelioration}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {preview && preview.total_eligibles > 0 && applied === null && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={confirmer}
                onChange={(e) => setConfirmer(e.target.checked)}
                className="rounded" />
              Je confirme — appliquer aux <b>{preview.total_eligibles}</b> note(s) de devoir
              ({preview.par_matiere.filter((m: any) => m.nb_eligibles > 0).length} matières)
            </label>
            <div className="flex gap-2">
              <button onClick={onClose}
                className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                Annuler
              </button>
              <button onClick={handleAppliquer}
                disabled={!confirmer || isLoading}
                className="px-3 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded font-medium flex items-center gap-1.5">
                {isLoading && <Loader size={12} className="animate-spin" />}
                Appliquer ({preview.total_eligibles})
              </button>
            </div>
          </div>
        )}

        {applied !== null && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-end">
            <button onClick={onClose}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function Kpi({ label, value, couleur, hint }: {
  label: string; value: number | undefined; couleur: string; hint?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-center">
      <div className={`text-lg font-bold tabular-nums ${couleur}`}>{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold mt-0.5">{label}</div>
      {hint && <div className="text-[9px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

export default ReporterExamenSiZeroBatchModal;
